import styles from './CodeVerificationModal.module.scss';
import ValidateButton from '../UI/ValidateButton';
import Close from '@src/assets/Close.png';
import CodeInput from '../UI/CodeInput';
import { useMemo, useState } from 'react';
import { generateRandomNumber, isDevOrRe7 } from '@src/utilities/functions';
import { useMutation } from '@tanstack/react-query';
import { verifyCodeFromEmail } from '@src/api/Registration';
import useRegistrationStore from '@src/stores/registrationStore';
import toast from 'react-hot-toast';

type ModalProps = {
    show: boolean;
    onClose: (value: boolean) => void;
    onValidate: () => void;
    retrySendCode: () => void;
    showAuthModal: () => void;
}

export default function CodeVerificationModal({ show, onClose, onValidate, retrySendCode, showAuthModal}: ModalProps) {
    const { registration } = useRegistrationStore();
    const [code, setCode] = useState<string>('');
    const [isRetryPending, setIsRetryPending] = useState<boolean>(false);
    const [retryTimer, setRetryTimer] = useState<number | null>(null);

    const randomCode = useMemo(() => generateRandomNumber(), [])

    const { mutate, isPending } = useMutation({
        mutationKey: ['verifyEmail'],
        mutationFn: (code: string) => verifyCodeFromEmail({email: registration.person.basic.email, code}),
        onSuccess: () => {
            onClose(true);
            onValidate();
        },
        onError: (error: any) => {
            if (error.response?.status === 400 && error.response?.data?.status === 'invalid') {
                toast.error('Le code saisi n\'est pas correct', { position: 'top-center' });
                setCode('');
            }
            if (error.response?.status === 400 && error.response?.data?.status === 'expired') {
                toast.error('Le code saisi est expiré', { position: 'top-center' });
                setCode('');
            }
        }
    })

    const _handleLogin = () => {
        onClose(true);
        showAuthModal();
    }

    const _handleRetry = async (e: any) => {
        e.preventDefault();
        if (isRetryPending || retryTimer !== null) return;
        setIsRetryPending(true);
        try {
            retrySendCode();
            let remainingTime = 30;
            setRetryTimer(remainingTime);
            const interval = setInterval(() => {
                remainingTime -= 1;
                setRetryTimer(remainingTime > 0 ? remainingTime : null);
                if (remainingTime <= 0) clearInterval(interval);
            }, 1000);
        } catch (err) {
            console.error('Erreur lors du renvoi du code:', err);
        } finally {
            setIsRetryPending(false);
        }
    };

    const _onValidate = () => {
        if (code.length === 6 && !isDevOrRe7()) {
            mutate(code);
        } else if (code === randomCode) {
            onClose(true);
            onValidate();
        }
    }
    const handleKeypress = (e: any) => {
        if (e.keyCode === 13) {
            _onValidate();
        }
    };

    if (!show) {
        return null;
    }
    return (
        <div className={styles.modalContainer}>
            <div className={styles.modal}>
                <div className={styles.close} onClick={() => onClose(true)}>
                    <img src={Close} alt="Close" width={14} height={14} />
                </div>
                <div className={styles.title}>Vérification de votre email</div>

                <div className={styles.contentContainer}>
                    <p className={styles.description}><strong>Vous avez déjà un compte membre ou membre premium ?</strong>&nbsp;
                        <button
                            onClick={_handleLogin}
                            className={styles.buttonLink}
                            style={{ color: '#24B1ED', cursor: 'pointer' }}
                        >
                            Connectez-vous ici
                        </button> pour accéder à votre espace.
                    </p>
                    <div style={{ width: '100%', height: '1px', backgroundColor: 'rgba(30, 30, 30, 0.25)', margin: '10px 0 10px 0' }} />
                    <p className={styles.description}><strong>Vous êtes nouveau ?</strong></p>
                    <p className={styles.description}>Un <strong>code à 6 chiffres</strong> a été envoyé à {registration.person.basic.email}.</p>
                    <p className={styles.description}><strong>Saisissez ce code ci-dessous</strong> pour poursuivre votre inscription :</p>
                    <div className={styles.codeInputContainer}>
                        <CodeInput fields={6} code={code} setCode={setCode} onKeyPress={handleKeypress} />
                    </div>
                    <p className={styles.description}><strong>Ce code est valable pendant 15 minutes</strong></p>
                    {!isDevOrRe7() ? (
                        <p className={styles.description} style={{ marginTop: '25px' }}>
                            Pas reçu ?{' '}
                            <button
                                onClick={_handleRetry}
                                className={styles.buttonLink}
                                style={{
                                    color: isRetryPending || retryTimer !== null ? '#333' : '#24B1ED',
                                    cursor: isRetryPending || retryTimer !== null ? 'not-allowed' : 'pointer',
                                }}
                                disabled={isRetryPending || retryTimer !== null}
                            >
                                {retryTimer !== null ? `Renvoyer un code dans ${retryTimer}s` : 'Renvoyer un code'}
                            </button>
                        </p>
                    ) : (
                        <p style={{color: 'red', textAlign: 'center', fontSize: '25px'}}>{randomCode}</p>
                    )}
                    <div className={styles.buttonDiv}>
                        <ValidateButton title="Valider" onClick={_onValidate} isModal onLoading={isPending} disabled={isPending || code.length !== 6} />
                    </div>
                </div>
            </div>
        </div>
    );
}
